import { CatalogueDatasource } from "@catalogue/data/datasource/catalogue.datasource";
import { Service } from "@catalogue/domain/models/service.model";
import { Pagination } from "@core/domain/models/pagination";
import { Nullable } from "@core/domain/types/nullable.type";
import { inject, injectable } from "inversify";
import { Derivation, CreateDerivation } from "@catalogue/domain/models/derivation.model";
import { DerivationSearchFilters } from "@catalogue/data/dto/derivation.dto"
import { CatalogueServiceError } from "@catalogue/domain/errors/catalogue-service.error";
import { Either } from "@core/domain/types/either";
import { DerivationMapperError } from "@catalogue/domain/errors/derivation/derivation-mapper.error";
import { UpdateDerivationData } from "@catalogue/domain/usecases/update-derivation.usecase";
import { CatalogueDerivationError } from "@catalogue/domain/errors/catalogue-derivation.error";
import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";

@injectable()
export class CatalogueRepository {
    constructor(
        @inject(CatalogueDatasource)
        private readonly datasource: CatalogueDatasource,
    ) { }

    async getAllServices(
        filters?: { name?: string; entityId?: number },
        pagination?: Pagination,
    ): Promise<Service[]> {
        const result = await this.datasource.fetchServices(pagination, filters);

        if (result.isLeft()) {
            ToastManagerStore.error(result.getLeftOrThrow().message);
        }

        return result.fold(
            () => [],
            (services) => services,
        );
    }

    async createService(
        service: Omit<Service, "id">,
    ): Promise<Nullable<Service>> {
        const result = await this.datasource.createService(service);

        if (result.isLeft()) {
            ToastManagerStore.error(result.getLeftOrThrow().message);
        }

        return result.fold(
            () => null,
            (createdService) => createdService,
        );
    }

    async getServiceById(serviceId: number): Promise<Nullable<Service>> {
        const result = await this.datasource.fetchServiceById(serviceId);

        if (result.isLeft()) {
            ToastManagerStore.error(result.getLeftOrThrow().message);
        }

        return result.fold(
            () => null,
            (service) => service,
        );
    }

    async deleteService(serviceId: number): Promise<Either<CatalogueServiceError, boolean>> {
        return this.datasource.deleteService(serviceId);
    }

    //DERIVATION
    async getAllDerivations(
        filters?: DerivationSearchFilters,
        pagination?: Pagination
    ): Promise<{ items: Derivation[], total: number }> {
        const result = await this.datasource.fetchDerivations(pagination, filters);

        if (result.isLeft()) {
            ToastManagerStore.error(result.getLeftOrThrow().message);
        }

        return result.fold(
            () => ({ items: [], total: 0 }),
            ({ derivations, count }) => ({ items: derivations, total: count }),
        );
    }

    async createDerivation(derivationData: CreateDerivation): Promise<Either<CatalogueDerivationError | DerivationMapperError, Derivation>> {
        return this.datasource.createDerivation(derivationData);
    }

    async getDerivationById(derivationId: number): Promise<Either<CatalogueDerivationError | DerivationMapperError, Derivation>> {
        return this.datasource.fetchDerivationById(derivationId);
    }

    async updateDerivation(derivationData: UpdateDerivationData): Promise<Either<CatalogueDerivationError | DerivationMapperError, Derivation>> {
        return this.datasource.updateDerivation(derivationData);
    }
}
